'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Copy, LogIn, UserPlus } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type AuthMode = 'login' | 'signup';
type UserRole = 'customer' | 'cleaner';

interface FormData {
  email: string;
  password: string;
  name?: string;
  confirmPassword?: string;
}

const demoCredentials = [
  { role: 'Customer', email: 'sarah.mitchell@email.com', password: 'cleanflow2026', href: '/customer-dashboard' },
  { role: 'Cleaner', email: 'marcus.johnson@cleaners.io', password: 'cleanflow2026', href: '/cleaner-dashboard' },
  { role: 'Admin', email: 'admin@cleanflow.io', password: 'admin2026', href: '/admin-dashboard' },
];

export default function AuthScreen() {
  const [mode, setMode] = useState<AuthMode>('login');
  const [role, setRole] = useState<UserRole>('customer');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { register, handleSubmit, setValue, formState: { errors }, watch } = useForm<FormData>();

  const password = watch('password');

  const handleDemoFill = (cred: typeof demoCredentials[0]) => {
    setValue('email', cred.email);
    setValue('password', cred.password);
    toast.success(`Demo credentials filled for ${cred.role}`);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    // Backend integration point: POST /api/auth/login or /api/auth/signup
    await new Promise((r) => setTimeout(r, 1200));
    setIsLoading(false);

    const matched = demoCredentials.find(c => c.email === data.email && c.password === data.password);
    if (matched) {
      toast.success(`Welcome back! Redirecting to ${matched.role} dashboard...`);
      setTimeout(() => { window.location.href = matched.href; }, 1000);
    } else {
      toast.error('Invalid credentials — use the demo accounts below to sign in');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 gradient-hero flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/3 right-0 w-80 h-80 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #0ea5e9, transparent)' }} />
        </div>
        <Link href="/" className="flex items-center gap-2 relative z-10">
          <AppLogo size={36} />
          <span className="font-bold text-2xl text-white">EzCleanCare</span>
        </Link>
        <div className="relative z-10 space-y-6">
          <h1 className="text-4xl font-extrabold text-white leading-tight">
            Professional Cleaning,<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-blue-300">
              Trusted Every Time
            </span>
          </h1>
          <p className="text-white/60 text-base leading-relaxed max-w-sm">
            Join 12,000+ homeowners and 800+ professional cleaners on the platform built for modern home care.
          </p>
          <div className="grid grid-cols-3 gap-4">
            {[['12K+', 'Customers'], ['800+', 'Cleaners'], ['4.9★', 'Rating']].map(([val, lbl]) => (
              <div key={`stat-${lbl}`} className="bg-white/10 rounded-xl p-3 text-center">
                <p className="text-xl font-extrabold text-white font-tabular">{val}</p>
                <p className="text-xs text-white/50 mt-0.5">{lbl}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-white/30 text-xs relative z-10">© 2026 EzCleanCare Inc.</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 sm:px-8 py-12 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Link href="/" className="flex items-center gap-2">
              <AppLogo size={32} />
              <span className="font-bold text-xl text-foreground">EzCleanCare</span>
            </Link>
          </div>

          <div className="mb-6">
            <Link href="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6">
              <ArrowLeft size={14} /> Back to home
            </Link>
            <h2 className="text-2xl font-extrabold text-foreground mb-1">
              {mode === 'login' ? 'Welcome back' : 'Create your account'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {mode === 'login' ? 'Sign in to manage your bookings' : 'Start booking in under 2 minutes'}
            </p>
          </div>

          {/* Mode toggle */}
          <div className="flex bg-muted rounded-xl p-1 mb-5">
            {(['login', 'signup'] as AuthMode[]).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${mode === m ? 'bg-card text-foreground card-shadow' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {m === 'login' ? <LogIn size={14} /> : <UserPlus size={14} />}
                {m === 'login' ? 'Sign In' : 'Sign Up'}
              </button>
            ))}
          </div>

          {/* Role selector */}
          <div className="flex bg-muted rounded-xl p-1 mb-5">
            {(['customer', 'cleaner'] as UserRole[]).map((r) => (
              <button
                key={`role-${r}`}
                onClick={() => setRole(r)}
                className={`flex-1 py-2 rounded-lg text-sm font-semibold capitalize transition-all duration-150 ${role === r ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {r}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Full Name</label>
                <input
                  type="text"
                  placeholder="Your full name"
                  className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all ${errors.name ? 'border-danger' : 'border-input'}`}
                  {...register('name', { required: 'Full name is required', minLength: { value: 2, message: 'Name must be at least 2 characters' } })}
                />
                {errors.name && <p className="mt-1 text-xs text-danger">{errors.name.message}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
              <input
                type="email"
                placeholder="you@example.com"
                className={`w-full px-4 py-3 rounded-xl border bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all ${errors.email ? 'border-danger' : 'border-input'}`}
                {...register('email', {
                  required: 'Email is required',
                  pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address' },
                })}
              />
              {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder={mode === 'signup' ? 'At least 8 characters' : 'Your password'}
                  className={`w-full px-4 py-3 pr-11 rounded-xl border bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all ${errors.password ? 'border-danger' : 'border-input'}`}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: mode === 'signup' ? { value: 8, message: 'Password must be at least 8 characters' } : undefined,
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-danger">{errors.password.message}</p>}
            </div>

            {mode === 'signup' && (
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm Password</label>
                <div className="relative">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    placeholder="Repeat your password"
                    className={`w-full px-4 py-3 pr-11 rounded-xl border bg-card text-foreground text-sm outline-none focus:ring-2 focus:ring-ring transition-all ${errors.confirmPassword ? 'border-danger' : 'border-input'}`}
                    {...register('confirmPassword', {
                      required: 'Please confirm your password',
                      validate: (v) => v === password || 'Passwords do not match',
                    })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  >
                    {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="mt-1 text-xs text-danger">{errors.confirmPassword.message}</p>}
              </div>
            )}

            {mode === 'login' && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                  <input type="checkbox" className="rounded border-input" />
                  Remember me
                </label>
                <button type="button" className="text-sm font-semibold text-primary hover:underline">Forgot password?</button>
              </div>
            )}

            {mode === 'signup' && (
              <p className="text-xs text-muted-foreground">
                By creating an account you agree to our{' '}
                <Link href="/" className="text-primary hover:underline">Terms of Service</Link>{' '}
                and{' '}
                <Link href="/" className="text-primary hover:underline">Privacy Policy</Link>.
              </p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 rounded-xl gradient-primary text-white font-semibold text-sm hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  {mode === 'login' ? 'Signing in...' : 'Creating account...'}
                </>
              ) : (
                mode === 'login' ? 'Sign In' : 'Create Account'
              )}
            </button>
          </form>

          {/* Demo credentials */}
          <div className="mt-6 rounded-xl border border-border bg-muted/50 p-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Demo Credentials — Click to Autofill</p>
            <div className="space-y-2">
              {demoCredentials.map((cred) => (
                <div key={`demo-${cred.role}`} className="flex items-center justify-between bg-card rounded-lg border border-border px-3 py-2 gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-foreground">{cred.role}</p>
                    <p className="text-xs text-muted-foreground truncate">{cred.email}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => copyToClipboard(cred.email)}
                      className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      aria-label="Copy email"
                    >
                      <Copy size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDemoFill(cred)}
                      className="px-2.5 py-1 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 active:scale-95 transition-all duration-150"
                    >
                      Use
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}